// src/components/AdminSidebar.jsx

import {
  Activity,
  ChevronDown,
  ChevronRight,
  FileText,
  Home,
  Package,
  Shield,
  ShoppingBag,
  Star,
  Tag,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

/**
 * Admin sidebar component with collapsible menu items
 * @param {Object} props - Component props
 * @param {boolean} props.sidebarOpen - Whether sidebar is open (mobile)
 * @param {Function} props.setSidebarOpen - Set sidebar open state
 * @returns {JSX.Element} AdminSidebar component
 */
const AdminSidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState(['store']); // Store expanded by default

  /**
   * Check if current path is active
   * @param {string} path - Path to check
   * @returns {boolean} True if path is active
   */
  const isActive = (path) => {
    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  /**
   * Check if any child path is active
   * @param {Array} children - Array of child menu items
   * @returns {boolean} True if any child is active
   */
  const isChildActive = (children) => {
    return children.some((child) => isActive(child.path));
  };

  /**
   * Toggle expanded state of a menu
   * @param {string} menuKey - Key of menu to toggle
   */
  const toggleMenu = (menuKey) => {
    setExpandedMenus((prev) =>
      prev.includes(menuKey)
        ? prev.filter((key) => key !== menuKey)
        : [...prev, menuKey]
    );
  };

  /**
   * Close sidebar (mobile)
   */
  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  /**
   * Navigation menu structure
   * @type {Array}
   */
  const navigationItems = [
    {
      name: 'Dashboard',
      path: '/admin/dashboard',
      icon: <Home className="h-5 w-5" />,
      single: true,
    },
    {
      name: 'Store Management',
      key: 'store',
      icon: <Package className="h-5 w-5" />,
      children: [
        {
          name: 'Categories',
          path: '/admin/store/categories',
          icon: <Tag className="h-4 w-4" />,
        },
        {
          name: 'Products',
          path: '/admin/store/products',
          icon: <ShoppingBag className="h-4 w-4" />,
        },
        {
          name: 'Orders',
          path: '/admin/store/orders',
          icon: <FileText className="h-4 w-4" />,
        },
        {
          name: 'Reviews',
          path: '/admin/store/reviews',
          icon: <Star className="h-4 w-4" />,
        },
        {
          name: 'Coupons',
          path: '/admin/store/coupons',
          icon: <Tag className="h-4 w-4" />,
        },
      ],
    },
    {
      name: 'User Management',
      key: 'users',
      icon: <Users className="h-5 w-5" />,
      children: [
        {
          name: 'All Users',
          path: '/admin/users',
          icon: <Users className="h-4 w-4" />,
        },
      ],
    },
    {
      name: 'Audit Logs',
      path: '/admin/audit-logs',
      icon: <Activity className="h-4 w-4" />,
      single: true,
    },
    // {
    //   name: 'Analytics',
    //   key: 'analytics',
    //   icon: <BarChart3 className="h-5 w-5" />,
    //   children: [
    //     {
    //       name: 'Sales Report',
    //       path: '/admin/analytics/sales',
    //       icon: <BarChart3 className="h-4 w-4" />,
    //     },
    //     {
    //       name: 'Product Analytics',
    //       path: '/admin/analytics/products',
    //       icon: <Package className="h-4 w-4" />,
    //     },
    //     {
    //       name: 'User Activity',
    //       path: '/admin/analytics/activity',
    //       icon: <Activity className="h-4 w-4" />,
    //     },
    //   ],
    // },
    // {
    //   name: 'Settings',
    //   path: '/admin/settings',
    //   icon: <Settings className="h-5 w-5" />,
    //   single: true,
    // },
  ];

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex-1 flex flex-col min-h-0 bg-slate-900 border-r border-slate-700">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              {/* Admin Identity */}
              <div className="flex items-center flex-shrink-0 px-6 pb-4 border-b border-slate-700">
                <Shield className="h-6 w-6 text-red-400 mr-2" />
                <div>
                  <div className="text-white font-semibold text-sm">
                    Admin Panel
                  </div>
                  <div className="text-gray-400 text-xs">
                    Management Dashboard
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <nav className="mt-5 flex-1 px-4 space-y-2">
                {navigationItems.map((item) => (
                  <div key={item.name}>
                    {item.single ? (
                      // Single menu item
                      <Link
                        to={item.path}
                        className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                          isActive(item.path)
                            ? 'bg-slate-900 text-white border-r-2 border-red-400'
                            : 'text-gray-300 hover:text-white hover:bg-slate-700'
                        }`}
                      >
                        <span
                          className={`mr-3 ${
                            isActive(item.path) ? 'text-red-400' : ''
                          }`}
                        >
                          {item.icon}
                        </span>
                        {item.name}
                      </Link>
                    ) : (
                      // Menu with children
                      <div>
                        <button
                          onClick={() => toggleMenu(item.key)}
                          className={`group flex items-center justify-between w-full px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                            isChildActive(item.children)
                              ? 'bg-slate-900 text-white border-r-2 border-red-400'
                              : 'text-gray-300 hover:text-white hover:bg-slate-700'
                          }`}
                        >
                          <div className="flex items-center">
                            <span
                              className={`mr-3 ${
                                isChildActive(item.children)
                                  ? 'text-red-400'
                                  : ''
                              }`}
                            >
                              {item.icon}
                            </span>
                            {item.name}
                          </div>
                          {expandedMenus.includes(item.key) ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>

                        {/* Submenu */}
                        {expandedMenus.includes(item.key) && (
                          <div className="mt-1 ml-8 space-y-1">
                            {item.children.map((child) => (
                              <Link
                                key={child.path}
                                to={child.path}
                                className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                                  isActive(child.path)
                                    ? 'bg-slate-900 text-white border-l-2 border-red-400'
                                    : 'text-gray-400 hover:text-white hover:bg-slate-700'
                                }`}
                              >
                                <span
                                  className={`mr-3 ${
                                    isActive(child.path) ? 'text-red-400' : ''
                                  }`}
                                >
                                  {child.icon}
                                </span>
                                {child.name}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </nav>
            </div>

            {/* Admin Status Footer */}
            <div className="flex-shrink-0 px-4 py-4 border-t border-slate-700">
              <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-lg p-3">
                <div className="flex items-center">
                  <Shield className="h-5 w-5 text-white mr-2" />
                  <div>
                    <div className="text-white text-sm font-semibold">
                      Admin Mode
                    </div>
                    <div className="text-red-100 text-xs">
                      Full system access
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <div
        className={`lg:hidden fixed inset-y-0 left-0 z-30 w-64 bg-slate-900 border-r border-slate-700 transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
            {/* Admin Identity - Mobile */}
            <div className="flex items-center flex-shrink-0 px-6 pb-4 border-b border-slate-700">
              <Shield className="h-6 w-6 text-red-400 mr-2" />
              <div>
                <div className="text-white font-semibold text-sm">
                  Admin Panel
                </div>
                <div className="text-gray-400 text-xs">
                  Management Dashboard
                </div>
              </div>
            </div>

            {/* Navigation - Mobile */}
            <nav className="mt-5 flex-1 px-4 space-y-2">
              {navigationItems.map((item) => (
                <div key={item.name}>
                  {item.single ? (
                    // Single menu item
                    <Link
                      to={item.path}
                      onClick={closeSidebar}
                      className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                        isActive(item.path)
                          ? 'bg-slate-900 text-white border-r-2 border-red-400'
                          : 'text-gray-300 hover:text-white hover:bg-slate-700'
                      }`}
                    >
                      <span
                        className={`mr-3 ${
                          isActive(item.path) ? 'text-red-400' : ''
                        }`}
                      >
                        {item.icon}
                      </span>
                      {item.name}
                    </Link>
                  ) : (
                    // Menu with children
                    <div>
                      <button
                        onClick={() => toggleMenu(item.key)}
                        className={`group flex items-center justify-between w-full px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                          isChildActive(item.children)
                            ? 'bg-slate-900 text-white border-r-2 border-red-400'
                            : 'text-gray-300 hover:text-white hover:bg-slate-700'
                        }`}
                      >
                        <div className="flex items-center">
                          <span
                            className={`mr-3 ${
                              isChildActive(item.children) ? 'text-red-400' : ''
                            }`}
                          >
                            {item.icon}
                          </span>
                          {item.name}
                        </div>
                        {expandedMenus.includes(item.key) ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>

                      {/* Submenu - Mobile */}
                      {expandedMenus.includes(item.key) && (
                        <div className="mt-1 ml-8 space-y-1">
                          {item.children.map((child) => (
                            <Link
                              key={child.path}
                              to={child.path}
                              onClick={closeSidebar}
                              className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                                isActive(child.path)
                                  ? 'bg-slate-900 text-white border-l-2 border-red-400'
                                  : 'text-gray-400 hover:text-white hover:bg-slate-700'
                              }`}
                            >
                              <span
                                className={`mr-3 ${
                                  isActive(child.path) ? 'text-red-400' : ''
                                }`}
                              >
                                {child.icon}
                              </span>
                              {child.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </div>

          {/* Admin Status Footer - Mobile */}
          <div className="flex-shrink-0 px-4 py-4 border-t border-slate-700">
            <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-lg p-3">
              <div className="flex items-center">
                <Shield className="h-5 w-5 text-white mr-2" />
                <div>
                  <div className="text-white text-sm font-semibold">
                    Admin Mode
                  </div>
                  <div className="text-red-100 text-xs">Full system access</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminSidebar;
